import React, { useMemo } from 'react';
import { formatMoney, normalizeCsRepDisplayNames } from './overviewCompute';

function formatRenewal(startDate, termMonths) {
  if (!startDate || !termMonths) return null;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + Number(termMonths));
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function daysBetweenToday(isoDay) {
  if (!isoDay) return null;
  const d = new Date(`${String(isoDay).slice(0, 10)}T00:00:00`);
  const a = new Date();
  a.setHours(0, 0, 0, 0);
  return Math.floor((a - d) / 86400000);
}

function agoLabel(isoDay) {
  const days = daysBetweenToday(isoDay);
  if (days == null) return isoDay || '—';
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 60) return `${days} days ago`;
  const d = new Date(`${String(isoDay).slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function OverviewTouchTab({ customer, loading, hasCustomerScope }) {
  const csrNames = useMemo(() => normalizeCsRepDisplayNames(customer || {}), [customer]);
  const qbrs = useMemo(() => {
    const list = customer?.qbrs || customer?.Qbrs || [];
    return [...list].sort((a, b) => {
      const da = String(a.meetingDate || a.MeetingDate || '');
      const db = String(b.meetingDate || b.MeetingDate || '');
      return db.localeCompare(da);
    });
  }, [customer]);

  const lastQbr = qbrs[0];
  const lastDay = lastQbr ? String(lastQbr.meetingDate || lastQbr.MeetingDate || '').slice(0, 10) : null;
  const daysSinceQbr = lastDay != null ? daysBetweenToday(lastDay) : null;
  // Cadence heuristic: hubs/monthly-ish → aim for a touch every ~90 days via QBR proxy.
  const cadence = 'Quarterly (QBR proxy)';
  const goal = 1;
  const done = lastDay && daysSinceQbr != null && daysSinceQbr <= 100 ? 1 : 0;
  const pct = Math.min(1, done / goal);
  const overdue = daysSinceQbr != null && daysSinceQbr > 90;
  const money = customer ? formatMoney(customer.paymentAmount, customer.paymentFrequency) : null;
  const renewal = customer
    ? formatRenewal(customer.contractStartDate, customer.contractTermMonths)
    : null;

  if (loading) return <div className="overview-loading">Loading touchpoints…</div>;

  if (!hasCustomerScope) {
    return (
      <div className="overview-empty">
        Touchpoints (QBRs, contract, CSR) require a <strong>customer</strong> scope.
        Filters and single facilities do not carry CRM records.
      </div>
    );
  }

  if (!customer) {
    return <div className="overview-empty">Customer detail not loaded.</div>;
  }

  return (
    <div className="overview-split3">
      <div className="overview-box">
        <h3>Cadence and renewal</h3>
        <div className="overview-big" style={{ fontSize: 18 }}>
          {cadence} · {done} of {goal} done
        </div>
        <div className="overview-progress">
          <i style={{ width: `${Math.round(pct * 100)}%`, background: pct >= 1 ? '#10b981' : '#f59e0b' }} />
        </div>
        <div className="overview-kv-row">
          <span>Last QBR</span>
          <b className={overdue ? 'warn' : ''}>
            {lastDay ? agoLabel(lastDay) : 'None logged'}
            {overdue ? ' · overdue' : ''}
          </b>
        </div>
        <div className="overview-kv-row">
          <span>Contract</span>
          <b>{money || '—'}</b>
        </div>
        <div className="overview-kv-row">
          <span>Renewal (computed)</span>
          <b>{renewal || '—'}</b>
        </div>
        <p className="overview-footnote">
          No HubSpot cadence or task API. Progress uses latest QBR meeting date as the touch proxy.
        </p>
      </div>

      <div className="overview-box">
        <h3>Recent activity</h3>
        {qbrs.length === 0 && !customer.notes ? (
          <p className="overview-sub">No QBRs or notes on this customer.</p>
        ) : (
          <>
            {qbrs.slice(0, 8).map(q => {
              const day = String(q.meetingDate || q.MeetingDate || '').slice(0, 10);
              const summary = q.summary || q.Summary || '';
              const rating = q.rating ?? q.Rating;
              return (
                <div key={q.id || day + summary} className="overview-tl">
                  <span className="overview-tl-ic" style={{ background: '#e0f2ec', color: '#0f7a5c' }}>Q</span>
                  <div className="grow">
                    <small>{agoLabel(day)} · QBR{rating != null ? ` · ${rating}/10` : ''}</small>
                    <div>{summary || '(no summary)'}</div>
                  </div>
                </div>
              );
            })}
            {customer.notes && (
              <div className="overview-tl">
                <span className="overview-tl-ic" style={{ background: '#eef2ff', color: '#4f46e5' }}>N</span>
                <div className="grow">
                  <small>Customer notes</small>
                  <div>{customer.notes}</div>
                </div>
              </div>
            )}
          </>
        )}
        <p className="overview-footnote">HubSpot timeline is not available.</p>
      </div>

      <div className="overview-box">
        <h3>
          Key contacts
          <span className="overview-pill gray" style={{ fontSize: 12, padding: '3px 10px' }}>
            VitalData CSRs
          </span>
        </h3>
        {csrNames.length === 0 ? (
          <p className="overview-sub">No CS rep names on this customer.</p>
        ) : (
          csrNames.map((name, i) => (
            <div key={name} className="overview-person">
              <span
                className="overview-avatar"
                style={{ background: ['#0c5a64', '#1e3a8a', '#0f7a5c'][i % 3] }}
              >
                {name.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase()}
              </span>
              <div className="grow">
                <b>{name}</b>
                <small>CS Rep</small>
              </div>
            </div>
          ))
        )}
        <p className="overview-footnote">
          HubSpot contact cards are not synced. Assign CSRs on Admin → Customers.
        </p>
      </div>
    </div>
  );
}

export default OverviewTouchTab;
